// Every English string on the production-plan screens (Stage 2A), in one
// place for a later Urdu table. Plain words for a busy kitchen.
import type { PlanStatus } from './planTypes'

export const PT = {
  tab: 'Plan cooking',
  newPlan: 'New plan',
  noPlans: 'No plans yet. Start one for your next event.',
  back: 'Back to plans',
  forDate: 'Cooking for',
  title: 'Event name (optional)',
  titlePlaceholder: 'Khan wedding, office lunch…',
  served: 'How is it served?',
  dineIn: 'Served at the venue',
  takeaway: 'Packed to go',
  dishes: 'Dishes',
  searchDishes: 'Search dishes…',
  noDishes: 'No dishes match.',
  addDishHint: 'Tap a dish to add it, then set how many.',
  portions: (n: number) => `${n} portion${n === 1 ? '' : 's'}`,
  dishCount: (n: number) => `${n} dish${n === 1 ? '' : 'es'}`,
  remove: 'Remove',
  enterHowMany: 'Enter how many',
  dishNeedsQty: 'Enter how many for every dish, or remove it.',
  notes: 'Notes (optional)',
  planNotes: 'Notes',
  customerNote: 'Customer note',
  bookingCancelled: 'BOOKING CANCELLED — check with the manager',
  bookingCancelledHelp: 'The booking was cancelled after the store sent stock, so this plan stayed. Do not cook it; send the stock back from this plan.',
  saveDraft: 'Save plan',
  saving: 'Saving…',
  saved: 'Plan saved',
  sheet: 'What this needs',
  sheetLive: 'Worked out from today’s recipes — confirm to lock it in.',
  sheetLocked: 'Locked when the plan was confirmed.',
  colItem: 'Item',
  colNeeded: 'Needed',
  colKitchen: 'Kitchen has',
  colStore: 'Store has',
  colAsk: 'Ask store for',
  colAsked: 'Asked store for',
  colMake: 'Make',
  colCost: 'Cost',
  estimatedCost: (amt: string) => `Estimated cost at average prices: ${amt}`,
  makeTitle: 'Make in the kitchen',
  makeNone: 'Nothing to prepare in advance.',
  runs: (n: string) => `${n} batch${n === '1' ? '' : 'es'}`,
  viaPrep: (names: string) => `for ${names}`,
  // Stage 2D: stock set aside for other events / expiring before this one.
  kitchenBreakdown: (total: string, reserved: string, expired: string) =>
    [`of ${total}`, reserved !== '0' ? `${reserved} set aside for other events` : '', expired !== '0' ? `${expired} expires before this event` : '']
      .filter(Boolean).join(' · '),
  kitchenFreeHint: 'Kitchen has = free for this event',
  // Go-live: on a locked plan the main figure is today's; the lock-time count underneath.
  countedOn: (qty: string, date: string) => (date ? `${qty} counted on ${date}` : `${qty} when locked`),
  warningsTitle: 'Check these',
  nothingYet: 'Add dishes to see what they need.',
  batchesTitle: 'Cooking planned',
  print: 'Print sheet',
  requestLink: (no: string, status: string) => `Store request ${no} — ${status}`,
  askHint: 'Change any amount before sending. 0 leaves an item out.',
  confirmHelp: 'Check the dishes, then lock the plan in.',
  // Batch 2: a dish with no recipe adds nothing to the sheet.
  noRecipe: 'No recipe',
  lockNoRecipe: (names: string) => `${names} ${names.includes(', ') ? 'have' : 'has'} no recipe — nothing will be asked from the store for ${names.includes(', ') ? 'them' : 'it'}. Lock anyway?`,
  lockAnyway: 'Lock anyway',
  goBack: 'Go back',
  actions: {
    confirm: 'Lock the plan',
    reopen: 'Change dishes',
    'send-to-store': 'Send to store',
    'plan-batches': 'Plan the cooking',
    start: 'Start cooking',
    done: 'All cooked',
    cancel: 'Cancel plan',
    'return-equipment': 'Send equipment back',
    'wrap-up': 'Event done — wrap up',
  },
  done: {
    confirm: 'Plan locked',
    reopen: 'Plan opened for changes',
    'send-to-store': 'Sent to the store',
    'plan-batches': 'Cooking planned',
    start: 'Cooking started',
    done: 'Cooking finished — now say what was used and what is left',
    cancel: 'Plan cancelled',
    'return-equipment': 'Equipment sent back — the store will check it',
    'wrap-up': '',
  },
  sentNoRequest: 'Sent — the kitchen already had everything, nothing asked from the store.',
  extrasTitle: 'Packaging & equipment',
  extrasHint: 'Boxes, foil, napkins, chafing dishes — anything the event needs that no recipe names.',
  searchItems: 'Search items…',
  noItemsMatch: 'No items match.',
  returnable: 'comes back',
  equipmentOut: 'Equipment still out',
  equipmentAllBack: 'All equipment is back.',
  sentOut: 'Sent',
  cameBack: 'Back',
  stillOut: 'Still out',
  sendBack: 'Send equipment back',
  sendBackDone: 'Equipment sent back — the store will check it',
  cancelReason: 'Why? (optional)',
  cancelConfirm: 'Yes, cancel it',
  keep: 'Keep it',
  status: {
    draft: 'Draft',
    confirmed: 'Locked',
    requisitioned: 'Sent to store',
    in_production: 'Cooking',
    done: 'Done',
    cancelled: 'Cancelled',
  } satisfies Record<PlanStatus, string>,
} as const
